import { Button, Col, Form, InputGroup } from 'react-bootstrap';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import MultiSelectDropdown from 'components/global/Field/FieldComponents/MultiSelectDropdown';
import { PROGRAM_KEYS } from 'components/global/Constant/constants';
import { SEDI_DEMOGRAPHICS_RELATED_BUSINESS_STATUS } from 'components/global/Constant/businessCompanyInfoDropDowns';
import type { LenderContactOption, RegisteredLenderOption } from '../../ApplicationDetail.helpers';
import type { ApplicationDetailsProgramKind } from './applicationDetailsProgram';
import type { ApplicationDetails } from './applicationDetailsViewModel';
import {
  CALCAP_FINANCIAL_FIELD_LABELS,
  CALCAP_PERCENT_POINT_FIELDS,
  PERCENTAGE_FIELDS,
  formatCalcapPercentPointsForDisplay,
  formatStoredPercentForDisplay,
  getFirstFormDataValue,
  getFormDataAsRecord,
} from './applicationDetailsViewModel';
import {
  ACTION_BUSINESS_DETAILS_KEY,
  BIZDATA_MIRROR_TO_STORAGE,
  CPCFA_SOURCED_FIELD_CONTROL_CLASS,
  DETAIL_LENDER_LOAN_NUMBER_KEY,
  DISPLAY_ADDRESS_LINE2_KEY,
  DISPLAY_BIZDATA_VETERAN_OWNED_KEY,
  DISPLAY_BORROWER_NAME_KEY,
  DISPLAY_BUSINESS_DBA_KEY,
  DISPLAY_BUSINESS_START_YEAR_KEY,
  DISPLAY_LENDER_CALCAP_ID_KEY,
  DISPLAY_LENDER_CONTACT_EMAIL_KEY,
  DISPLAY_LENDER_CONTACT_PHONE_KEY,
  DISPLAY_LENDER_NAME_KEY,
  LENDER_CALCAP_ID_STORAGE_KEY,
  BIZDATA_LOAN_ADDRESS_LINE1_KEY,
  BIZDATA_LOAN_CITY_KEY,
  BIZDATA_LOAN_COUNTY_KEY,
  BIZDATA_LOAN_ZIP_KEY,
} from './applicationDetailsWireframeKeys';
import { tryRenderVariantApplicationField } from './applicationDetailsFieldVariants';

const LOAN_PURPOSE_SEPARATOR = ';';

const SEDI_CIRCUMSTANCES_MULTI_OPTIONS = Object.entries(SEDI_DEMOGRAPHICS_RELATED_BUSINESS_STATUS)
  .filter(([k]) => k !== 'n/a')
  .map(([, v]) => ({ value: v.value, label: v.label }));

const ETHNICITY_OPTIONS = [
  { value: '', label: '-- Select Ethnicity --' },
  { value: 'HISPANIC_OR_LATINO', label: 'Hispanic or Latino' },
  { value: 'NOT_HISPANIC_OR_LATINO', label: 'Not Hispanic or Latino' },
  { value: 'PREFER_NOT_TO_RESPOND', label: 'Prefer not to respond' },
];

// Race multi-select: value and label are the description (stored in DB as description, separated by ;)
const RACE_OPTIONS_MULTI = [
  { value: 'American Indian or Alaska Native', label: 'American Indian or Alaska Native' },
  { value: 'Asian', label: 'Asian' },
  { value: 'Indian', label: 'Indian' },
  { value: 'Chinese', label: 'Chinese' },
  { value: 'Filipino', label: 'Filipino' },
  { value: 'Japanese', label: 'Japanese' },
  { value: 'Korean', label: 'Korean' },
  { value: 'Vietnamese', label: 'Vietnamese' },
  { value: 'Asian (Other)', label: 'Asian (Other)' },
  { value: 'Black or African American', label: 'Black or African American' },
  { value: 'Native Hawaiian or Other Pacific Islander', label: 'Native Hawaiian or Other Pacific Islander' },
  { value: 'Guamanian or Chamorro', label: 'Guamanian or Chamorro' },
  { value: 'Native Hawaiian', label: 'Native Hawaiian' },
  { value: 'Samoan', label: 'Samoan' },
  { value: 'Pacific Islander(Other)', label: 'Pacific Islander(Other)' },
  { value: 'White', label: 'White' },
  { value: 'Prefer not to respond', label: 'Prefer not to respond' },
];

// Map legacy codes and old enum values to description (for parsing existing DB values)
const RACE_CODE_OR_LEGACY_TO_DESCRIPTION: Record<string, string> = {
  AI: 'American Indian or Alaska Native',
  A: 'Asian',
  I: 'Indian',
  C: 'Chinese',
  F: 'Filipino',
  J: 'Japanese',
  K: 'Korean',
  V: 'Vietnamese',
  AO: 'Asian (Other)',
  B: 'Black or African American',
  N: 'Native Hawaiian or Other Pacific Islander',
  G: 'Guamanian or Chamorro',
  NH: 'Native Hawaiian',
  S: 'Samoan',
  PI: 'Pacific Islander(Other)',
  W: 'White',
  P: 'Prefer not to respond',
  AMERICAN_INDIAN_OR_ALASKA_NATIVE: 'American Indian or Alaska Native',
  ASIAN_INDIAN: 'Indian',
  ASIAN_CHINESE: 'Chinese',
  ASIAN_FILIPINO: 'Filipino',
  ASIAN_JAPANESE: 'Japanese',
  ASIAN_KOREAN: 'Korean',
  ASIAN_VIETNAMESE: 'Vietnamese',
  ASIAN_OTHER: 'Asian (Other)',
  BLACK_OR_AFRICAN: 'Black or African American',
  GUAMANIAN_OR_CHAMORRO: 'Guamanian or Chamorro',
  NATIVE_HAWAIIAN: 'Native Hawaiian',
  SAMOAN: 'Samoan',
  PACIFIC_ISLANDER_OTHER: 'Pacific Islander(Other)',
  WHITE: 'White',
  PREFER_NOT_TO_RESPOND: 'Prefer not to respond',
};

const RACE_SEPARATOR = ';';

const ANCESTRY_OPTIONS = [
  { value: '', label: '-- Select Ancestry --' },
  { value: 'MIDDLE_EASTERN_OR_NORTH_AFRICAN_ANCESTRY', label: 'Middle Eastern or North African' },
  { value: 'NOT_MIDDLE_EASTERN_OR_NORTH_AFRICAN_ANCESTRY', label: 'Not Middle Eastern or North African' },
  { value: 'PREFER_NOT_TO_RESPOND', label: 'Prefer not to respond' },
];

const GENDER_OPTIONS = [
  { value: '', label: '-- Select Gender --' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'MALE', label: 'Male' },
  { value: 'NONBINARY', label: 'Nonbinary' },
  { value: 'PREFER_TO_SELF_DESCRIBE', label: 'Prefer to self-describe' },
];

const ORIENTATION_OPTIONS = [
  { value: '', label: '-- Select Sexual Orientation --' },
  { value: 'GAY_OR_LESBIAN', label: 'Gay or Lesbian' },
  { value: 'BISEXUAL', label: 'Bisexual' },
  { value: 'STRAIGHT', label: 'Straight' },
  { value: 'SOMETHING_ELSE', label: 'Something else' },
  { value: 'PREFER_NOT_TO_RESPOND', label: 'Prefer not to respond' },
];

// Y/N/D stored in DB; display descriptions in dropdown (used for veteranOwned, disabledVeteran)
const VETERAN_YN_D_OPTIONS = [
  { value: '', label: '-- Select --' },
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
  { value: 'D', label: 'Declined' },
];

const GENDER_VALUE_MAP: Record<string, string> = {
  'F': 'FEMALE',
  'M': 'MALE',
  'N': 'NONBINARY',
  'PS': 'PREFER_TO_SELF_DESCRIBE',
};

const ORIENTATION_VALUE_MAP: Record<string, string> = {
  'GL': 'GAY_OR_LESBIAN',
  'B': 'BISEXUAL',
  'S': 'STRAIGHT',
  'SE': 'SOMETHING_ELSE',
  'P': 'PREFER_NOT_TO_RESPOND',
};

const ETHNICITY_VALUE_MAP: Record<string, string> = {
  'H': 'HISPANIC_OR_LATINO',
  'NH': 'NOT_HISPANIC_OR_LATINO',
  'P': 'PREFER_NOT_TO_RESPOND',
};

const ANCESTRY_VALUE_MAP: Record<string, string> = {
  'M': 'MIDDLE_EASTERN_OR_NORTH_AFRICAN_ANCESTRY',
  'NM': 'NOT_MIDDLE_EASTERN_OR_NORTH_AFRICAN_ANCESTRY',
  'P': 'PREFER_NOT_TO_RESPOND',
};

const LOAN_TYPE_OPTIONS = [
  { value: '', label: '-- Select Loan Type --' },
  { value: 'L', label: 'L - Line of Credit' },
  { value: 'T', label: 'T - Term Loan' },
  { value: 'R', label: 'R - TRAC Lease' },
];

const DELIVERY_METHOD_OPTIONS = [
  { value: '', label: '-- Select Delivery Method --' },
  { value: 'M', label: 'M - Mail' },
  { value: 'E', label: 'E - Email' },
  { value: 'F', label: 'F - Fax' },
  { value: 'I', label: 'I - Internet' },
  { value: 'C', label: 'C - CAAT Lender Portal' },
];

/** Wireframe: SAC Reason (FRED-style codes) */
const SAC_REASON_OPTIONS = [
  { value: '', label: '-- Select SAC Reason --' },
  { value: 'EZ', label: 'EZ — Enterprise Zone' },
  { value: 'HU', label: 'HU — High Unemployment' },
  { value: 'NA', label: 'NA — N/A' },
  { value: 'DA', label: 'DA — Disaster Area' },
  { value: 'CV', label: 'CV — COVID-19' },
];

/** FRED LOA_INT_RATE_TYPE */
const INTEREST_RATE_TYPE_OPTIONS = [
  { value: '', label: '-- Select --' },
  { value: 'F', label: 'F — Fixed' },
  { value: 'V', label: 'V — Variable' },
];

/** Change in Terms — LOA_TERM_PURPOSE */
const CIT_PURPOSE_OPTIONS = [
  { value: '', label: '-- Select Purpose --' },
  { value: 'MD', label: 'MD - Maturity Date' },
  { value: 'I', label: 'I - Interest' },
  { value: 'BN', label: 'BN - Borrower Name' },
  { value: 'O', label: 'O - Other' },
];

const formatLenderContactLabel = (c: LenderContactOption): string => {
  const name =
    (c.name && c.name.trim()) ||
    [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
    `Contact #${c.id}`;
  const phone = [c.phoneAreaCode, c.phoneNumber].filter(Boolean).join(' ').trim();
  const tail = (c.email && c.email.trim()) || phone || '';
  const role = c.contactRole?.trim() ? ` — ${c.contactRole.trim()}` : '';
  return tail ? `${name} (${tail})${role}` : `${name}${role}`;
};

const getSelectedLenderContact = (
  contacts: LenderContactOption[],
  contactId: unknown
): LenderContactOption | undefined => {
  if (contactId === null || contactId === undefined || contactId === '') return undefined;
  const n = Number(contactId);
  if (Number.isNaN(n)) return undefined;
  return contacts.find((c) => c.id === n);
};
/** Wireframe: Minority / Woman / Veteran ownership — dropdowns (not checkboxes) */
const YES_NO_DECLINE_OPTIONS = [
  { value: '', label: '-- Select --' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'decline', label: 'Decline to Answer' },
];
/** Row 1 of Business Information: 4 columns (Business ID, Borrower, DBA, button). */
const BUSINESS_INFO_ROW1_FIELDS = new Set([
  'businessId',
  DISPLAY_BORROWER_NAME_KEY,
  DISPLAY_BUSINESS_DBA_KEY,
  ACTION_BUSINESS_DETAILS_KEY,
]);

/** SAC Supplemental row: wider address, narrower qualifying event (wireframe). */
const SAC_SUPPLEMENTAL_COL_OVERRIDES: Record<string, { md: number; lg: number }> = {
  formData_cdfiInvestmentAreaAddress: { md: 8, lg: 8 },
  formData_sacQualifyingEvent: { md: 4, lg: 4 },
};

/** SEDI section wireframe: row widths (12-column grid). */
const SEDI_WIREFRAME_COL_OVERRIDES: Record<string, { md: number; lg: number }> = {
  /** Row 1: Not certified | Owned credit | Circumstances (wider on large screens). */
  sedi_businessNotCertified: { md: 4, lg: 3 },
  sedi_businessOwnedCredit: { md: 4, lg: 3 },
  formData_sediCircumstances: { md: 4, lg: 6 },
  /** Row 2: Owned CDFI | Business owned address */
  sedi_businessOwnedCDFI: { md: 4, lg: 3 },
  formData_sediBusinessOwnedAddress: { md: 6, lg: 9 },
  /** Row 3: Business build (syncs build + build area) | Business build address */
  sedi_businessBuild: { md: 6, lg: 3 },
  formData_sediBusinessBuildAddress: { md: 6, lg: 9 },
};

/** Other Information: full-width blob, then Created / Updated side by side. */
const OTHER_INFORMATION_COL_OVERRIDES: Record<string, { md: number; lg: number }> = {
  cpcfaImportBlob: { md: 12, lg: 12 },
  createdDate: { md: 6, lg: 6 },
  updatedDate: { md: 6, lg: 6 },
};

const getStandardFieldColProps = (fieldName: string): { md: number; lg: number } => {
  if (OTHER_INFORMATION_COL_OVERRIDES[fieldName]) return OTHER_INFORMATION_COL_OVERRIDES[fieldName];
  if (SAC_SUPPLEMENTAL_COL_OVERRIDES[fieldName]) return SAC_SUPPLEMENTAL_COL_OVERRIDES[fieldName];
  if (SEDI_WIREFRAME_COL_OVERRIDES[fieldName]) return SEDI_WIREFRAME_COL_OVERRIDES[fieldName];
  return BUSINESS_INFO_ROW1_FIELDS.has(fieldName) ? { md: 6, lg: 3 } : { md: 6, lg: 4 };
};

const getBusinessDetailsPath = (
  businessId: unknown,
  portalType: string | undefined,
  portalTypes: { business: string; lender: string; vc: string; edo: string }
): string | null => {
  const id = businessId != null && businessId !== '' ? Number(businessId) : NaN;
  if (Number.isNaN(id)) return null;
  if (portalType === portalTypes.lender) {
    return `/lender/search-businesses/business-search-results/${id}`;
  }
  if (portalType === portalTypes.vc) {
    return `/vc/search-businesses/business-search-results/${id}`;
  }
  if (portalType === portalTypes.edo) {
    return `/edo/business/${id}/edit`;
  }
  if (portalType === portalTypes.business) {
    return '/business/organization';
  }
  return null;
};

const getLenderRecordFromDetails = (details: ApplicationDetails | null): Record<string, any> | null => {
  if (!details) return null;
  return details.lender || details.fundingProgramMembership?.lender || null;
};

/** When there are no Contact rows: show name from digital form JSON, then lender.contactName. */
const getLenderContactNameFromFormOrLender = (
  details: ApplicationDetails | null | undefined
): string => {
  if (!details) return '';
  const fd = getFormDataAsRecord(details.formData);
  if (fd) {
    const v = getFirstFormDataValue(fd, [
      'lenderinformation_contact_name',
      'lenderInformation_contactName',
      'lenderContactName',
      'loaninformation_lender_contact_name',
      'lender_information_contact_name',
    ]);
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  const lender = getLenderRecordFromDetails(details);
  const cn = lender?.contactName;
  if (cn !== undefined && cn !== null && String(cn).trim() !== '') {
    return String(cn).trim();
  }
  return '';
};
const TRIBAL_GOVERNMENT_PROGRAM_TRANSACTION_TYPE_OPTIONS = [
  { value: '', label: '-- Select Tribal Government Program Transaction Type --' },
  { value: 'IN_JURISDICTION_BUSINESS_TRIBAL_LANDS', label: 'In-jurisdiction transaction with business on tribal lands' },
  { value: 'IN_JURISDICTION_BUSINESS_STATES_TRIBE_PHYSICALLY_LOCATED', label: 'In-jurisdiction transaction with business in states where the Tribe is physically located or within which the Tribe exercises jurisdiction' },
  { value: 'IN_JURISDICTION_TRIBAL_ENTERPRISE_OPERATED_BUSINESS', label: 'In-jurisdiction transaction with Tribal enterprise-operated business, business owned by Tribal members, or business in a state in which Tribal members reside' },
  { value: 'OUT_OF_JURISDICTION', label: 'Out-of-jurisdiction transaction - transaction which does not fit into any of the categories above' },
  { value: 'NOT_APPLICABLE', label: 'N/A' },
];

const PRIMARY_PURPOSE_OPTIONS = [
  { value: 'Marketing, market research, and commercialization expenses', label: 'Marketing, market research, and commercialization expenses' },
  { value: 'Research and development', label: 'Research and development' },
  { value: 'Technology integration in physical production, e.g., manufacturing or supply chain', label: 'Technology integration in physical production, e.g., manufacturing or supply chain' },
  { value: 'Technology integration of nonphysical production, e.g., accounting, customers', label: 'Technology integration of nonphysical production, e.g., accounting, customers' },
  { value: 'Acquire land', label: 'Acquire land' },
  { value: 'Purchase existing building', label: 'Purchase existing building' },
  { value: 'Convert, expand, or renovate buildings - energy efficiency', label: 'Convert, expand, or renovate buildings - energy efficiency' },
  { value: 'Convert, expand, or renovate buildings - other', label: 'Convert, expand, or renovate buildings - other' },
  { value: 'Construct new buildings', label: 'Construct new buildings' },
  { value: 'Acquire and install fixed assets - equipment', label: 'Acquire and install fixed assets - equipment' },
  { value: 'Acquire and install fixed assets - other', label: 'Acquire and install fixed assets - other' },
  { value: 'Acquire inventory', label: 'Acquire inventory' },
  { value: 'Purchase supplies and raw materials', label: 'Purchase supplies and raw materials' },
  { value: 'Leasehold improvements', label: 'Leasehold improvements' },
  { value: 'Working capital - wages, salaries, and benefits of employees', label: 'Working capital - wages, salaries, and benefits of employees' },
  { value: 'Working capital - other', label: 'Working capital - other' },
  { value: 'Support employee stock ownership plan (ESOP) transactions', label: 'Support employee stock ownership plan (ESOP) transactions' },
  { value: 'Other', label: 'Other' },
];

const SECONDARY_PURPOSE_OPTIONS = [
  { value: 'Marketing, market research, and commercialization expenses', label: 'Marketing, market research, and commercialization expenses' },
  { value: 'Research and development', label: 'Research and development' },
  { value: 'Technology integration in physical production, e.g., manufacturing or supply chain', label: 'Technology integration in physical production, e.g., manufacturing or supply chain' },
  { value: 'Technology integration of nonphysical production, e.g., accounting, customers', label: 'Technology integration of nonphysical production, e.g., accounting, customers' },
  { value: 'Acquire land', label: 'Acquire land' },
  { value: 'Purchase existing building', label: 'Purchase existing building' },
  { value: 'Convert, expand, or renovate buildings - energy efficiency', label: 'Convert, expand, or renovate buildings - energy efficiency' },
  { value: 'Convert, expand, or renovate buildings - other', label: 'Convert, expand, or renovate buildings - other' },
  { value: 'Construct new buildings', label: 'Construct new buildings' },
  { value: 'Acquire and install fixed assets - equipment', label: 'Acquire and install fixed assets - equipment' },
  { value: 'Acquire and install fixed assets - other', label: 'Acquire and install fixed assets - other' },
  { value: 'Acquire inventory', label: 'Acquire inventory' },
  { value: 'Purchase supplies and raw materials', label: 'Purchase supplies and raw materials' },
  { value: 'Leasehold improvements', label: 'Leasehold improvements' },
  { value: 'Working capital - wages, salaries, and benefits of employees', label: 'Working capital - wages, salaries, and benefits of employees' },
  { value: 'Working capital - other', label: 'Working capital - other' },
  { value: 'Refinance outstanding debt', label: 'Refinance outstanding debt' },
  { value: 'Support employee stock ownership plan (ESOP) transactions', label: 'Support employee stock ownership plan (ESOP) transactions' },
  { value: 'Other', label: 'Other' },
  { value: 'No secondary Purpose', label: 'No secondary Purpose' },
];

const getDateValue = (dateValue: any): string => {
  if (!dateValue) return '';
  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return dateValue.toISOString().split('T')[0];
  }
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    return dateValue.split('T')[0];
  }
  if (typeof dateValue === 'string' || typeof dateValue === 'number') {
    return String(dateValue);
  }
  return '';
};

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  /** Application.status in DB (workflow state); not Lender.applicationStatus. */
  status: 'Application status',
  cpcfaImportBlob: 'CPCFA Import Blob',
  createdDate: 'Created Date',
  updatedDate: 'Updated Date',
  technicalAssistanceId: 'Technical Assistance ID',
  askedAmount: 'Total Loan Amount',
  approvedAmount: 'Enrolled Amount',
  borrowerEquity: '% of Loan Enrolled in CalCAP',
  borrowerPremiumAmount: 'Borrower Percentage (%)',
  calcapPremiumAmount: 'Lender Fee ($)',
  calcap: 'Lender Percentage (%)',
  calcapAmount: 'CalCAP Combined Rate (%)',
  calcapPercentage: 'Borrower Fee ($)',
  premiumAmount: 'Premium Amount',
  totalOriginationCharges: 'Origination Charges',
  loanAPR: 'Loan APR',
  interestRate: 'Interest Rate',
  termPurpose: 'Other Repayment Terms',
  citReceived: 'CIT Received',
  citReceivedDate: 'Received Date',
  citPurpose: 'Purpose',
  citChangeEffectiveDate: 'Change Effective Date',
  citApproveReject: 'CIT Approved or Rejected',
  tribalGovernmentProgramTransactionType: 'Tribal Government Program Transaction Type',
  loanPurpose: 'Primary Purpose of the Loan',
  loanPurposeOther: 'Primary Purpose of the Loan - Other',
  loanPurposeSecondary: 'Secondary Purpose of the Loan',
  loanPurposeSecondaryOther: 'Secondary Purpose of the Loan - Other',
  loanType: 'Type of Loan',
  loanTypeOther: 'Loan Type Other',
  governmentGuaranteeProgram: 'Any other government guarantee program?',
  guaranteeProgramName: 'Other Guarantee Program Name',
  otherProgramPercentage: '% of Loan Enrolled in Another Program?',
  secured: 'Loan Secured',
  refinancing: 'Restructure of Prior CalCAP Loan?',
  formData_businessOrgType: 'Form of Business',
  formData_businessCounty: 'County',
  formData_averageAnnualRevenueLast3Years: 'Avg Annual Rev. Last 3 Years',
  formData_inSacOrSedi: 'Is the business located in a Severely Affected Community (SAC)?',
  formData_sacReason: 'SAC Reason?',
  formData_loanStartDate: 'Date of Loan',
  businessData_severelyAffected: 'Severely Affected',
  formData_interestRateType: 'Interest Rate Type',
  formData_usedAtAddress: 'Will Loan Monies be used at above address?',
  formData_calcapLoanNumPrior: 'Previous CalCAP Loan #',
  formData_remainBalanceRestructure: 'Remaining Balance of Restructured Loan',
  formData_amountIncreaseRequested: 'Amount of Increase',
  formData_cspAppNumber: '(For Collateral Support) CSP App#',
  businessData_annualRevenue: 'Annual Rev Last Fiscal Year',
  formData_lenderContactId: 'Lender Contact Name',
  lenderEin: 'Lender EIN/Tax ID',
  lenderId: 'Registered lender',
  [DISPLAY_LENDER_NAME_KEY]: 'Lender Name',
  [DISPLAY_LENDER_CALCAP_ID_KEY]: 'Lender CalCAP ID',
  [DISPLAY_LENDER_CONTACT_PHONE_KEY]: 'Lender Contact Phone Number',
  [DISPLAY_LENDER_CONTACT_EMAIL_KEY]: 'Lender Contact Email',
  businessEin: 'Business EIN/Tax ID',
  businessData_addressLine1: 'Address Line 1',
  businessData_city: 'City',
  businessData_zipCode: 'Zip Code',
  businessData_naicsCode: 'NAICS Code',
  businessData_numberOfFtes: 'Number of Full Time Equivalents',
  businessData_expectedJobsCreated: 'Number of Jobs Created',
  businessData_expectedJobsRetained: 'Number of Jobs Retained',
  businessData_censusTractNumber: 'Census Tract Number',
  businessData_minorityOwned: 'Minority Owned',
  businessData_womenOwned: 'Women Owned',
  businessData_minorityOrWomenOwned: 'Minority Or Women Owned',
  businessData_county: 'County',
  businessData_countyCode: 'County Code',
  businessData_ein: 'EIN',
  businessData_numberOfEmployees: 'Number Of Employees',
  businessData_numberOfJobs: 'Number Of Jobs',
  businessData_businessOpened2Years: 'Business Opened 2+ Years',
  businessData_nonProfit: 'Non Profit',
  jobsCreated: 'Jobs Created',
  jobsRetained: 'Jobs Retained',
  calcapLoanNumber: 'CalCAP Loan #',
  [BIZDATA_LOAN_ADDRESS_LINE1_KEY]: 'Loan Monies Used: Address',
  [BIZDATA_LOAN_CITY_KEY]: 'Loan Monies Used: City',
  [BIZDATA_LOAN_COUNTY_KEY]: 'Loan Monies Used: County',
  [BIZDATA_LOAN_ZIP_KEY]: 'Loan Monies Used: Zip',
  [DISPLAY_BIZDATA_VETERAN_OWNED_KEY]: 'Business Veteran Owned?',
  [DISPLAY_BORROWER_NAME_KEY]: 'Borrower Name',
  [DISPLAY_BUSINESS_DBA_KEY]: 'Business Name (DBA)',
  [DISPLAY_ADDRESS_LINE2_KEY]: 'Address (line 2)',
  [DISPLAY_BUSINESS_START_YEAR_KEY]: 'Business Start Year',
  sedi_businessNotCertified: 'Business Not Certified',
  sedi_businessOwnedCredit: 'Business Owned Credit',
  sedi_businessOwnedCDFI: 'Business Owned CDFI',
  sedi_businessBuild: 'Business build',
  sedi_businessBuildArea: 'Business build',
  formData_sediCircumstances: 'Circumstances',
  formData_sediBusinessOwnedAddress: 'Business owned address',
  formData_sediBusinessBuildAddress: 'Business build address',
  formData_cdfiInvestmentAreaAddress: 'CDFI Investment Area Address',
  formData_sacQualifyingEvent: 'Qualifying Event',
  /** Jul 23: show program name only (Funding Program ID field hidden). */
  programName: 'Funding Program',
};

const formatDisplayName = (
  fieldName: string,
  isCalcapProgram = false,
  useOcrCalcapLabels = false
): string => {
  if (isCalcapProgram && useOcrCalcapLabels && CALCAP_FINANCIAL_FIELD_LABELS[fieldName]) {
    return CALCAP_FINANCIAL_FIELD_LABELS[fieldName];
  }
  if (FIELD_LABEL_OVERRIDES[fieldName]) {
    return FIELD_LABEL_OVERRIDES[fieldName];
  }
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/\bEin\b/gi, 'EIN')
    .replace(/\bId\b/g, 'ID')
    .replace(/\bNaics\b/gi, 'NAICS')
    .replace(/\bFtes\b/gi, 'FTEs')
    .replace(/\bC D F I\b/gi, 'CDFI')
    .replace(/\bCalcap\b/gi, 'CalCAP')
    .replace(/\bEdo\b/gi, 'EDO')
    .replace(/\bSedi\b/gi, 'SEDI')
    .replace(/\bCpcfa\b/gi, 'CPCFA')
    .trim();
};

/** Subset of FundingProgram used when resolving CalCAP vs CSP and display labels in the details tab. */
export type ApplicationDetailsFundingProgramRef =
  | {
      name?: string | null;
      referenceProgram?: { key?: string | null };
    }
  | null
  | undefined;

export type ApplicationDetailsFieldRendererDeps = {
  programKind: ApplicationDetailsProgramKind;
  excludedFields: ReadonlySet<string>;
  readOnlyFields: ReadonlySet<string>;
  draftDetails: ApplicationDetails;
  applicationDetails: ApplicationDetails | null;
  fieldErrors: Record<string, string>;
  isContentLoading: boolean;
  isSaving: boolean;
  edoUsers: any[];
  lenderContacts: LenderContactOption[];
  /** EDO lenders from GET /v2/lenders; when non-empty enables lender reassignment dropdown. */
  registeredLendersForSelect: RegisteredLenderOption[];
  getFundingProgram: () => ApplicationDetailsFundingProgramRef;
  isAmountField: (fieldName: string) => boolean;
  parseCurrency: (value: string) => number | null;
  handleFieldChange: (fieldName: string, value: any) => void;
  amountFieldRawValues: Record<string, string>;
  setAmountFieldRawValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  focusedAmountFields: Set<string>;
  setFocusedAmountFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  focusedPercentageFields: Set<string>;
  setFocusedPercentageFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  percentageFieldRawValues: Record<string, string>;
  setPercentageFieldRawValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  focusedCalcapPercentPointFields: Set<string>;
  setFocusedCalcapPercentPointFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  calcapPercentPointRawValues: Record<string, string>;
  setCalcapPercentPointRawValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  lenderApplicationDetailsReadOnly: boolean;
  APPLICATION_STATUS: Record<string, string>;
  portalType: string | undefined;
  PORTAL_TYPES: { business: string; lender: string; vc: string; edo: string };
};

export function useApplicationDetailsFieldRenderer(deps: ApplicationDetailsFieldRendererDeps) {
  const {
    programKind,
    excludedFields,
    readOnlyFields,
    draftDetails,
    applicationDetails,
    fieldErrors,
    isContentLoading,
    isSaving,
    edoUsers,
    lenderContacts,
    registeredLendersForSelect,
    getFundingProgram,
    isAmountField,
    parseCurrency,
    handleFieldChange,
    amountFieldRawValues,
    setAmountFieldRawValues,
    focusedAmountFields,
    setFocusedAmountFields,
    focusedPercentageFields,
    setFocusedPercentageFields,
    percentageFieldRawValues,
    setPercentageFieldRawValues,
    focusedCalcapPercentPointFields,
    setFocusedCalcapPercentPointFields,
    calcapPercentPointRawValues,
    setCalcapPercentPointRawValues,
    lenderApplicationDetailsReadOnly,
    APPLICATION_STATUS,
    portalType,
    PORTAL_TYPES,
  } = deps;
  const history = useHistory();

  const renderFieldError = useCallback((fieldError: string | undefined) => {
    if (!fieldError) return null;
    return <Form.Text className="text-danger">{fieldError}</Form.Text>;
  }, []);

  const renderField = useCallback((fieldName: string, value: any) => {
    if (excludedFields.has(fieldName)) return null;

    const variantNode = tryRenderVariantApplicationField(programKind, {
      fieldName,
      value,
      draftDetails,
      applicationDetails,
    });
    if (variantNode !== undefined && variantNode !== null) return variantNode;

    if (fieldName === DETAIL_LENDER_LOAN_NUMBER_KEY) {
      const fp = getFundingProgram();
      const isCalcapLoanNum =
        fp?.referenceProgram?.key === PROGRAM_KEYS.capitalAccess;
      const storageKey = isCalcapLoanNum ? 'calcapLoanNumber' : 'lenderLoanNumber';
      const fieldError = fieldErrors[storageKey];
      const label = isCalcapLoanNum ? 'CalCAP Loan #' : 'Lender loan number';
      return (
        <Col key={DETAIL_LENDER_LOAN_NUMBER_KEY} md={6} lg={4} className="mb-3">
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type="text"
            value={draftDetails[storageKey] ?? ''}
            onChange={(e) => handleFieldChange(storageKey, e.target.value || null)}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    const isCalcapProgram =
      getFundingProgram()?.referenceProgram?.key === PROGRAM_KEYS.capitalAccess;
    /** CalCAP financial fields use percent-point inputs for OCR, CPCFA import, and digital form. */
    const isCalcapPercentPointProgram = isCalcapProgram;

    const isReadOnly =
      readOnlyFields.has(fieldName) || lenderApplicationDetailsReadOnly;
    const fieldError = fieldErrors[fieldName];
    // Format display name (use full fieldName for businessData_* so FIELD_LABEL_OVERRIDES match).
    let displayName = fieldName;
    if (fieldName.startsWith('businessData_')) {
      displayName = formatDisplayName(
        fieldName,
        isCalcapProgram,
        isCalcapProgram
      );
    } else if (fieldName === 'loaId') {
      displayName = 'LOA ID';
    } else if (fieldName.startsWith('demographics_')) {
      displayName = formatDisplayName(fieldName.replace('demographics_', ''));
    } else if (fieldName.startsWith('sedi_') || fieldName.startsWith('formData_sedi')) {
      displayName = formatDisplayName(
        fieldName,
        isCalcapProgram,
        isCalcapProgram
      );
    } else {
      displayName = formatDisplayName(
        fieldName,
        isCalcapProgram,
        isCalcapProgram
      );
    }

    const labelContent = <>{displayName}</>;

    // Get the actual value from draftDetails for prefixed fields
    const actualValue = draftDetails[fieldName] ?? value;

    if (fieldName === ACTION_BUSINESS_DETAILS_KEY) {
      const bid = applicationDetails?.businessId ?? applicationDetails?.business?.id;
      const path = getBusinessDetailsPath(bid, portalType, PORTAL_TYPES);
      const cp = getStandardFieldColProps(fieldName);
      return (
        <Col key={fieldName} md={cp.md} lg={cp.lg} className="mb-3 d-flex align-items-end">
          <Button
            variant="secondary"
            className="w-100"
            disabled={!path || isContentLoading}
            onClick={() => path && history.push(path)}
            type="button"
          >
            Business Details
          </Button>
        </Col>
      );
    }

    if (
      fieldName === DISPLAY_BORROWER_NAME_KEY ||
      fieldName === DISPLAY_BUSINESS_DBA_KEY ||
      fieldName === DISPLAY_ADDRESS_LINE2_KEY ||
      fieldName === DISPLAY_BUSINESS_START_YEAR_KEY
    ) {
      const raw = draftDetails[fieldName];
      const v = raw === null || raw === undefined ? '' : String(raw);
      const cp = getStandardFieldColProps(fieldName);
      return (
        <Col key={fieldName} md={cp.md} lg={cp.lg} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="text"
            value={v}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              if (isReadOnly || isContentLoading) return;
              handleFieldChange(fieldName, e.target.value);
            }}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    const mirrorStorageKey = BIZDATA_MIRROR_TO_STORAGE[fieldName];
    if (mirrorStorageKey) {
      const raw = draftDetails[mirrorStorageKey];
      const v = raw === null || raw === undefined ? '' : String(raw);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="text"
            value={v}
            disabled
            readOnly
            className={CPCFA_SOURCED_FIELD_CONTROL_CLASS}
          />
        </Col>
      );
    }

    if (fieldName === DISPLAY_BIZDATA_VETERAN_OWNED_KEY) {
      const storedValue = draftDetails.demographics_veteranOwned;
      const yndValue =
        typeof storedValue === 'string' && ['Y', 'N', 'D'].includes(storedValue) ? storedValue : '';
      const veteranFieldError = fieldErrors[fieldName] || fieldErrors.demographics_veteranOwned;
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={yndValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) =>
              handleFieldChange('demographics_veteranOwned', e.target.value || null)
            }
            isInvalid={!!veteranFieldError}
          >
            {VETERAN_YN_D_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(veteranFieldError)}
        </Col>
      );
    }

    if (fieldName === 'formData_businessCounty') {
      const raw =
        draftDetails.formData_businessCounty ?? draftDetails.businessData_county ?? '';
      const cp = getStandardFieldColProps(fieldName);
      return (
        <Col key={fieldName} md={cp.md} lg={cp.lg} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="text"
            value={raw === null || raw === undefined ? '' : String(raw)}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              if (isReadOnly || isContentLoading) return;
              handleFieldChange(fieldName, e.target.value);
            }}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'status') {
      const statusOptions = Object.values(APPLICATION_STATUS) as string[];
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={draftDetails[fieldName] ?? ''}
            disabled
            isInvalid={!!fieldError}
          >
            <option value="">-- Select status --</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'citReceived') {
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Check
            type="checkbox"
            id={fieldName}
            label={labelContent}
            checked={!!draftDetails[fieldName]}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'citPurpose') {
      const raw = draftDetails[fieldName];
      const selectVal =
        raw === null || raw === undefined || raw === ''
          ? ''
          : CIT_PURPOSE_OPTIONS.some((o) => o.value === String(raw))
            ? String(raw)
            : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={selectVal}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {CIT_PURPOSE_OPTIONS.map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'citApproveReject') {
      const current = draftDetails[fieldName] ?? '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label className="d-block">{labelContent}</Form.Label>
          <div className="d-flex flex-wrap gap-3">
            <Form.Check
              type="radio"
              name="citApproveReject"
              id="citApproveReject-approved"
              label="Approved"
              checked={current === 'Approved'}
              disabled={isReadOnly || isContentLoading}
              onChange={() => handleFieldChange(fieldName, 'Approved')}
            />
            <Form.Check
              type="radio"
              name="citApproveReject"
              id="citApproveReject-rejected"
              label="Rejected"
              checked={current === 'Rejected'}
              disabled={isReadOnly || isContentLoading}
              onChange={() => handleFieldChange(fieldName, 'Rejected')}
            />
          </div>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'formData_sacReason') {
      const rawVal = draftDetails[fieldName];
      const raw = rawVal === null || rawVal === undefined ? '' : String(rawVal).trim();
      const exactOpt = SAC_REASON_OPTIONS.find((o) => o.value !== '' && o.value === raw);
      let selectVal = '';
      if (exactOpt) {
        selectVal = exactOpt.value;
      } else if (raw === '') {
        selectVal = '';
      } else {
        const upper = raw.toUpperCase();
        const codeFromWord = upper.match(/\b(EZ|HU|NA|DA|CV)\b/)?.[1];
        const twoLetter =
          codeFromWord ||
          (upper.length >= 2 && ['EZ', 'HU', 'NA', 'DA', 'CV'].includes(upper.slice(0, 2))
            ? upper.slice(0, 2)
            : '');
        const matchOpt =
          SAC_REASON_OPTIONS.find((o) => o.value && o.value === twoLetter) ||
          SAC_REASON_OPTIONS.find(
            (o) =>
              o.value &&
              (upper.includes(o.value) ||
                o.label.toUpperCase().includes(upper) ||
                raw.includes(o.value))
          );
        selectVal = matchOpt?.value ?? '';
      }
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={selectVal}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
            className={CPCFA_SOURCED_FIELD_CONTROL_CLASS}
          >
            {SAC_REASON_OPTIONS.map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'formData_interestRateType') {
      const v = String(draftDetails[fieldName] ?? '')
        .trim()
        .toUpperCase();
      const sel = v === 'F' || v === 'V' ? v : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={sel}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {INTEREST_RATE_TYPE_OPTIONS.map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'formData_usedAtAddress') {
      const current = String(draftDetails[fieldName] ?? '').trim();
      const isYes =
        current.toLowerCase() === 'yes' ||
        current.toLowerCase() === 'y' ||
        current.toLowerCase() === 'true';
      const isNo =
        current.toLowerCase() === 'no' ||
        current.toLowerCase() === 'n' ||
        current.toLowerCase() === 'false';
      if (lenderApplicationDetailsReadOnly) {
        const textVal = isYes ? 'TRUE' : isNo ? 'FALSE' : '';
        return (
          <Col key={fieldName} md={6} lg={4} className="mb-3">
            <Form.Label>{labelContent}</Form.Label>
            <Form.Control type="text" value={textVal} disabled readOnly isInvalid={!!fieldError} />
            {renderFieldError(fieldError)}
          </Col>
        );
      }
      const normalized = isYes ? 'Yes' : isNo ? 'No' : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={normalized}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            <option value="">-- Select --</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName.toLowerCase().includes('date')) {
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="date"
            value={getDateValue(draftDetails[fieldName])}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'demographics_veteranOwned' || fieldName === 'demographics_disabledVeteran') {
      const storedValue = draftDetails[fieldName];
      const yndValue = typeof storedValue === 'string' && ['Y', 'N', 'D'].includes(storedValue) ? storedValue : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={yndValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {VETERAN_YN_D_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'businessData_severelyAffected') {
      const b = draftDetails[fieldName];
      const textVal = b === true ? 'TRUE' : b === false ? 'FALSE' : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control type="text" value={textVal} disabled readOnly isInvalid={!!fieldError} />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'businessData_censusTractNumber') {
      const raw = draftDetails[fieldName];
      const v = raw === null || raw === undefined ? '' : String(raw);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="text"
            value={v}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              const digits = e.target.value.replace(/\./g, '').replace(/[^\d]/g, '');
              handleFieldChange(fieldName, digits || null);
            }}
            isInvalid={!!fieldError}
          />
          <Form.Text className="text-muted">Numeric only (removes &quot;.&quot;)</Form.Text>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'businessData_countyCode') {
      const raw = draftDetails[fieldName];
      const v = raw === null || raw === undefined || raw === '' ? '' : String(raw);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="number"
            value={v}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              const x = e.target.value;
              handleFieldChange(fieldName, x === '' ? null : Number(x));
            }}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'businessData_minorityOwned' || fieldName === 'businessData_womenOwned') {
      const v = draftDetails[fieldName];
      const strVal = v === true ? 'yes' : v === false ? 'no' : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={strVal}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              const x = e.target.value;
              if (x === '' || x === 'decline') {
                handleFieldChange(fieldName, false);
              } else {
                handleFieldChange(fieldName, x === 'yes');
              }
            }}
            isInvalid={!!fieldError}
          >
            {YES_NO_DECLINE_OPTIONS.map((option) => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'sedi_businessBuild') {
      const combined = !!(
        draftDetails.sedi_businessBuild || draftDetails.sedi_businessBuildArea
      );
      const cp = getStandardFieldColProps(fieldName);
      const buildErr = fieldErrors[fieldName] || fieldErrors.sedi_businessBuildArea;
      return (
        <Col key={fieldName} md={cp.md} lg={cp.lg} className="mb-3">
          <Form.Check
            type="checkbox"
            id={fieldName}
            label={labelContent}
            checked={combined}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
          />
          {renderFieldError(buildErr)}
        </Col>
      );
    }

    if (fieldName === 'formData_sediCircumstances') {
      const raw = draftDetails[fieldName] ?? '';
      const str = raw === null || raw === undefined ? '' : String(raw);
      const cp = getStandardFieldColProps(fieldName);
      return (
        <Col key={fieldName} md={cp.md} lg={cp.lg} className="mb-3">
          <MultiSelectDropdown
            name={fieldName}
            label={displayName}
            value={str}
            options={SEDI_CIRCUMSTANCES_MULTI_OPTIONS}
            separator={LOAN_PURPOSE_SEPARATOR}
            onChange={(nextValue: string | null) => handleFieldChange(fieldName, nextValue)}
            disabled={isReadOnly || isContentLoading}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    // Handle boolean fields - check actual value type or field name patterns
    const isBooleanField =
      (typeof actualValue === 'boolean' && fieldName !== 'businessData_severelyAffected') ||
      (fieldName.startsWith('sedi_') && fieldName !== 'sedi_businessBuildArea') ||
      (fieldName.startsWith('businessData_') &&
        ((fieldName.includes('Owned') &&
          fieldName !== 'businessData_minorityOwned' &&
          fieldName !== 'businessData_womenOwned') ||
          fieldName.includes('Opened') ||
          fieldName.includes('Profit') ||
          (fieldName.includes('Affected') && fieldName !== 'businessData_severelyAffected') ||
          fieldName.includes('Veteran')));

    if (isBooleanField) {
      const colProps = getStandardFieldColProps(fieldName);
      return (
        <Col key={fieldName} md={colProps.md} lg={colProps.lg} className="mb-3">
          <Form.Check
            type="checkbox"
            id={fieldName}
            label={labelContent}
            checked={draftDetails[fieldName] ?? false}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return (
        <Col key={fieldName} md={12} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={JSON.stringify(draftDetails[fieldName] || {}, null, 2)}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleFieldChange(fieldName, parsed);
              } catch {
                handleFieldChange(fieldName, e.target.value);
              }
            }}
            isInvalid={!!fieldError}
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
          />
          {renderFieldError(fieldError)}
          <Form.Text className="text-muted">JSON format</Form.Text>
        </Col>
      );
    }

    if (Array.isArray(value)) {
      return (
        <Col key={fieldName} md={12} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={JSON.stringify(draftDetails[fieldName] || [], null, 2)}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleFieldChange(fieldName, parsed);
              } catch {
                handleFieldChange(fieldName, e.target.value);
              }
            }}
            isInvalid={!!fieldError}
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
          />
          {renderFieldError(fieldError)}
          <Form.Text className="text-muted">JSON array format</Form.Text>
        </Col>
      );
    }

    if (fieldName === 'businessId') {
      const cp = getStandardFieldColProps(fieldName);
      return (
        <Col key={fieldName} md={cp.md} lg={cp.lg} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="number"
            value={draftDetails[fieldName] ?? ''}
            disabled={true}
            readOnly
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'lenderId') {
      const allowLenderReassign =
        registeredLendersForSelect.length > 0 &&
        portalType === PORTAL_TYPES.edo &&
        !lenderApplicationDetailsReadOnly &&
        !isContentLoading;

      const lenderSelectRows = (() => {
        const map = new Map<number, RegisteredLenderOption>();
        registeredLendersForSelect.forEach((l) => map.set(l.id, l));
        const lid = draftDetails?.lenderId ?? applicationDetails?.lenderId;
        if (
          lid != null &&
          lid !== '' &&
          !Number.isNaN(Number(lid)) &&
          !map.has(Number(lid))
        ) {
          const lr = getLenderRecordFromDetails(applicationDetails);
          const name =
            lr != null && Number(lr.id) === Number(lid) ? lr.name ?? null : null;
          map.set(Number(lid), {
            id: Number(lid),
            name: name ?? `Lender #${lid}`,
          });
        }
        return Array.from(map.values()).sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
        );
      })();

      if (allowLenderReassign) {
        const vid = draftDetails.lenderId;
        const val =
          vid != null && vid !== '' && !Number.isNaN(Number(vid)) ? String(vid) : '';
        const fieldErrorLender = fieldErrors.lenderId;
        return (
          <Col key={fieldName} md={6} lg={4} className="mb-3">
            <Form.Label>{labelContent}</Form.Label>
            <Form.Control
              as="select"
              value={val}
              disabled={isSaving}
              onChange={(e) => {
                const raw = e.target.value;
                handleFieldChange('lenderId', raw === '' ? null : Number(raw));
              }}
              isInvalid={!!fieldErrorLender}
            >
              <option value="">-- Select registered lender --</option>
              {lenderSelectRows.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.name?.trim() ? `${l.name.trim()} (ID ${l.id})` : `Lender #${l.id}`}
                </option>
              ))}
            </Form.Control>
            {renderFieldError(fieldErrorLender)}
          </Col>
        );
      }

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="number"
            value={draftDetails[fieldName] ?? ''}
            disabled
            readOnly
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === DISPLAY_LENDER_NAME_KEY) {
      const lidNum =
        draftDetails?.lenderId != null &&
        draftDetails.lenderId !== '' &&
        !Number.isNaN(Number(draftDetails.lenderId))
          ? Number(draftDetails.lenderId)
          : null;
      const fromSelect =
        lidNum != null
          ? registeredLendersForSelect.find((x) => x.id === lidNum)
          : undefined;
      const lender = getLenderRecordFromDetails(applicationDetails);
      const nm =
        (fromSelect?.name ?? '').trim() ||
        (lender != null && lidNum != null && Number(lender.id) === lidNum
          ? String(lender.name ?? '').trim()
          : '');
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control type="text" value={nm} disabled readOnly />
        </Col>
      );
    }

    if (fieldName === DISPLAY_LENDER_CALCAP_ID_KEY) {
      const v = draftDetails[LENDER_CALCAP_ID_STORAGE_KEY] ?? '';
      const fieldErrorStored = fieldErrors[LENDER_CALCAP_ID_STORAGE_KEY];
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="text"
            value={v}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(LENDER_CALCAP_ID_STORAGE_KEY, e.target.value)}
            placeholder="YR-Sequence (04-0060)"
            isInvalid={!!fieldErrorStored}
          />
          {renderFieldError(fieldErrorStored)}
        </Col>
      );
    }

    if (fieldName === DISPLAY_LENDER_CONTACT_PHONE_KEY) {
      const sel = getSelectedLenderContact(lenderContacts, draftDetails.formData_lenderContactId);
      let phone = sel
        ? [sel.phoneAreaCode, sel.phoneNumber].filter(Boolean).join(' ').trim()
        : '';
      if (!phone && lenderContacts.length === 0) {
        const lender = getLenderRecordFromDetails(applicationDetails);
        phone = (lender?.phoneNumber && String(lender.phoneNumber).trim()) || '';
      }
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control type="text" value={phone} disabled readOnly />
        </Col>
      );
    }

    if (fieldName === DISPLAY_LENDER_CONTACT_EMAIL_KEY) {
      const sel = getSelectedLenderContact(lenderContacts, draftDetails.formData_lenderContactId);
      let em = sel?.email?.trim() ?? '';
      if (!em && lenderContacts.length === 0) {
        const lender = getLenderRecordFromDetails(applicationDetails);
        em = (lender?.email && String(lender.email).trim()) || '';
      }
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control type="text" value={em} disabled readOnly />
        </Col>
      );
    }

    if (fieldName === 'fundingProgramId') {
      const fundingProgram = getFundingProgram();
      const programName = fundingProgram?.name ||
                        (fundingProgram && typeof fundingProgram === 'object' ? 'Program (name not available)' : 'N/A');

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="number"
            value={draftDetails[fieldName] ?? ''}
            disabled={true}
            readOnly
          />
          <Form.Text muted style={{ fontSize: '1rem' }}>
            Funding Program: <strong style={{ fontSize: '1rem' }}>{programName}</strong>
          </Form.Text>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'programName') {
      const fundingProgram = getFundingProgram();
      const programName = fundingProgram?.name ||
                        (fundingProgram && typeof fundingProgram === 'object' ? 'Program (name not available)' : 'N/A');

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="text"
            value={programName}
            disabled={true}
            readOnly
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'loanType') {
      const loanTypeValue = draftDetails[fieldName];

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={loanTypeValue || ''}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {LOAN_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'tribalGovernmentProgramTransactionType') {
      const tribalValue = draftDetails[fieldName];

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={tribalValue || ''}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {TRIBAL_GOVERNMENT_PROGRAM_TRANSACTION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'formData_lenderContactId') {
      const raw = draftDetails[fieldName];
      const selectVal = raw !== null && raw !== undefined && raw !== '' ? String(raw) : '';
      const effectiveLenderId = draftDetails?.lenderId ?? applicationDetails?.lenderId;
      const hasLender =
        effectiveLenderId != null &&
        effectiveLenderId !== '' &&
        !Number.isNaN(Number(effectiveLenderId));
      const hasContactRows = lenderContacts.length > 0;
      const disabledSelect =
        isContentLoading || !hasLender || lenderApplicationDetailsReadOnly;

      const fallbackName =
        getLenderContactNameFromFormOrLender(draftDetails) ||
        getLenderContactNameFromFormOrLender(applicationDetails);

      if (!hasContactRows && hasLender) {
        return (
          <Col key={fieldName} md={6} lg={4} className="mb-3">
            <Form.Label>{labelContent}</Form.Label>
            <Form.Control
              type="text"
              value={fallbackName}
              disabled
              readOnly
              placeholder="From application form or lender profile"
            />
            {renderFieldError(fieldError)}
          </Col>
        );
      }

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={selectVal}
            disabled={disabledSelect}
            onChange={(e) => {
              const v = e.target.value;
              handleFieldChange(fieldName, v === '' ? null : Number(v));
            }}
            isInvalid={!!fieldError}
          >
            <option value="">
              {!hasLender ? '-- Assign a lender first --' : '-- Select lender contact --'}
            </option>
            {lenderContacts.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {formatLenderContactLabel(c)}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'deliveryMethod') {
      const deliveryMethodValue = draftDetails[fieldName];

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={deliveryMethodValue || ''}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {DELIVERY_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'receivedById') {
      const receivedByIdValue = draftDetails[fieldName];
      const selectValue =
        receivedByIdValue === null || receivedByIdValue === undefined || receivedByIdValue === ''
          ? ''
          : String(receivedByIdValue);

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={selectValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) =>
              handleFieldChange(
                fieldName,
                e.target.value === '' ? null : String(e.target.value)
              )
            }
            isInvalid={!!fieldError}
          >
            <option value="">-- Select EDO User --</option>
            {edoUsers.map((user: any) => {
              const userDisplayName = user.displayName ||
                                 `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                                 user.id;
              return (
                <option key={user.id} value={String(user.id)}>
                  {userDisplayName}
                </option>
              );
            })}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    const findMatchingOptionValue = (
      storedValue: string,
      options: Array<{value: string, label: string}>,
      valueMap?: Record<string, string>
    ): string => {
      if (!storedValue) return '';

      const exactMatch = options.find(opt => opt.value === storedValue);
      if (exactMatch) return exactMatch.value;

      if (valueMap) {
        const mappedValue = valueMap[storedValue];
        if (mappedValue) {
          const mappedOption = options.find(opt => opt.value === mappedValue);
          if (mappedOption) return mappedOption.value;
        }
      }

      return '';
    };

    // Handle demographics dropdown fields
    if (fieldName === 'demographics_ethnicity') {
      const storedValue = draftDetails[fieldName] || '';
      const ethnicityValue = findMatchingOptionValue(storedValue, ETHNICITY_OPTIONS, ETHNICITY_VALUE_MAP);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={ethnicityValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {ETHNICITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'demographics_race') {
      const toDescription = (s: string) => {
        const trimmed = (s || '').trim();
        if (!trimmed) return '';
        return RACE_CODE_OR_LEGACY_TO_DESCRIPTION[trimmed] ?? trimmed;
      };
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <MultiSelectDropdown
            name={fieldName}
            label={displayName}
            value={draftDetails[fieldName] || ''}
            options={RACE_OPTIONS_MULTI}
            separator={RACE_SEPARATOR}
            onChange={(nextValue: string | null) => handleFieldChange(fieldName, nextValue)}
            disabled={isReadOnly || isContentLoading}
            displayTransform={toDescription}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'demographics_ancestry') {
      const storedValue = draftDetails[fieldName] || '';
      const ancestryValue = findMatchingOptionValue(storedValue, ANCESTRY_OPTIONS, ANCESTRY_VALUE_MAP);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={ancestryValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {ANCESTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'demographics_gender') {
      const storedValue = draftDetails[fieldName] || '';
      const genderValue = findMatchingOptionValue(storedValue, GENDER_OPTIONS, GENDER_VALUE_MAP);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={genderValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {GENDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'demographics_orientation') {
      const storedValue = draftDetails[fieldName] || '';
      const orientationValue = findMatchingOptionValue(storedValue, ORIENTATION_OPTIONS, ORIENTATION_VALUE_MAP);
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={orientationValue}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            {ORIENTATION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'formData_inSacOrSedi') {
      const current = String(draftDetails[fieldName] ?? '').trim();
      const normalized =
        current.toLowerCase() === 'yes' || current.toLowerCase() === 'y' || current.toLowerCase() === 'true'
          ? 'Yes'
          : current.toLowerCase() === 'no' || current.toLowerCase() === 'n' || current.toLowerCase() === 'false'
            ? 'No'
            : '';
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="select"
            value={normalized}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          >
            <option value="">-- Select --</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </Form.Control>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (isCalcapPercentPointProgram && CALCAP_PERCENT_POINT_FIELDS.has(fieldName)) {
      const rawStored = draftDetails[fieldName];
      const pctFocused = focusedCalcapPercentPointFields.has(fieldName);
      const pctRaw = calcapPercentPointRawValues[fieldName];
      const displayValue =
        !isReadOnly && pctFocused && pctRaw !== undefined
          ? pctRaw
          : formatCalcapPercentPointsForDisplay(rawStored);

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={displayValue}
              disabled={isReadOnly || isContentLoading}
              onFocus={() => {
                if (isReadOnly || isContentLoading) return;
                setFocusedCalcapPercentPointFields((prev) => new Set(prev).add(fieldName));
                setCalcapPercentPointRawValues((prev) => ({
                  ...prev,
                  [fieldName]: formatCalcapPercentPointsForDisplay(draftDetails[fieldName]),
                }));
              }}
              onBlur={() => {
                setFocusedCalcapPercentPointFields((prev) => {
                  const next = new Set(prev);
                  next.delete(fieldName);
                  return next;
                });
                const raw = calcapPercentPointRawValues[fieldName] ?? '';
                const cleaned = raw.replace(/[%\s]/g, '');
                setCalcapPercentPointRawValues((prev) => {
                  const next = { ...prev };
                  delete next[fieldName];
                  return next;
                });
                if (cleaned === '') {
                  handleFieldChange(fieldName, null);
                  return;
                }
                const parsed = parseFloat(cleaned);
                if (isNaN(parsed)) return;
                handleFieldChange(fieldName, parsed);
              }}
              onChange={(e) => {
                if (isReadOnly || isContentLoading) return;
                const s = e.target.value.replace(/[%\s]/g, '');
                if (s === '' || /^-?\d*\.?\d*$/.test(s)) {
                  setCalcapPercentPointRawValues((prev) => ({ ...prev, [fieldName]: s }));
                }
              }}
              placeholder="0.00"
              isInvalid={!!fieldError}
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (
      PERCENTAGE_FIELDS.has(fieldName) &&
      !(isCalcapProgram && fieldName === 'calcapPercentage')
    ) {
      const rawStored = draftDetails[fieldName];
      const pctFocused = focusedPercentageFields.has(fieldName);
      const pctRaw = percentageFieldRawValues[fieldName];
      const displayValue =
        !isReadOnly && pctFocused && pctRaw !== undefined
          ? pctRaw
          : formatStoredPercentForDisplay(rawStored);

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              inputMode="decimal"
              value={displayValue}
              disabled={isReadOnly || isContentLoading}
              onFocus={() => {
                if (isReadOnly || isContentLoading) return;
                setFocusedPercentageFields((prev) => new Set(prev).add(fieldName));
                setPercentageFieldRawValues((prev) => ({
                  ...prev,
                  [fieldName]: formatStoredPercentForDisplay(draftDetails[fieldName]),
                }));
              }}
              onBlur={() => {
                setFocusedPercentageFields((prev) => {
                  const next = new Set(prev);
                  next.delete(fieldName);
                  return next;
                });
                const raw = percentageFieldRawValues[fieldName] ?? '';
                const cleaned = raw.replace(/[%\s]/g, '');
                setPercentageFieldRawValues((prev) => {
                  const next = { ...prev };
                  delete next[fieldName];
                  return next;
                });
                if (cleaned === '') {
                  handleFieldChange(fieldName, null);
                  return;
                }
                const parsed = parseFloat(cleaned);
                if (isNaN(parsed)) return;
                handleFieldChange(fieldName, parsed > 1 ? parsed / 100 : parsed);
              }}
              onChange={(e) => {
                if (isReadOnly || isContentLoading) return;
                const s = e.target.value.replace(/[%\s]/g, '');
                if (s === '' || /^-?\d*\.?\d*$/.test(s)) {
                  setPercentageFieldRawValues((prev) => ({ ...prev, [fieldName]: s }));
                }
              }}
              placeholder="0.00"
              isInvalid={!!fieldError}
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (isAmountField(fieldName)) {
      const amountValue = draftDetails[fieldName] ?? null;
      const isFocused = focusedAmountFields.has(fieldName);

      let displayValue: string;
      /** While focused, keep showing the in-progress string even when empty — otherwise clearing the input snaps back to the formatted stored value. */
      const rawFocused = amountFieldRawValues[fieldName];
      const shouldShowRawValue = isFocused && rawFocused !== undefined && !isSaving;

      if (shouldShowRawValue) {
        displayValue = rawFocused ?? '';
      } else {
        let numValue: number | null = null;
        if (typeof amountValue === 'number') {
          numValue = amountValue;
        } else if (amountValue !== null && amountValue !== undefined) {
          numValue = Number(amountValue);
        }

        if (numValue !== null && !isNaN(numValue) && isFinite(numValue)) {
          displayValue = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(numValue);
        } else {
          displayValue = '';
        }
      }

      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="text"
              value={displayValue}
              disabled={isReadOnly || isContentLoading}
              onFocus={() => {
                setFocusedAmountFields(prev => new Set(prev).add(fieldName));
                if (amountValue !== null && amountValue !== undefined && !isNaN(amountValue)) {
                  const rawValue = amountValue.toFixed(2);
                  setAmountFieldRawValues(prev => ({ ...prev, [fieldName]: rawValue }));
                } else {
                  setAmountFieldRawValues(prev => ({ ...prev, [fieldName]: '' }));
                }
              }}
              onBlur={() => {
                setFocusedAmountFields(prev => {
                  const next = new Set(prev);
                  next.delete(fieldName);
                  return next;
                });
                const rawValue = amountFieldRawValues[fieldName] ?? '';
                const numValue = parseCurrency(rawValue);
                if (numValue !== null && !isNaN(numValue)) {
                  handleFieldChange(fieldName, numValue);
                } else {
                  handleFieldChange(fieldName, null);
                }
                setAmountFieldRawValues(prev => {
                  const next = { ...prev };
                  delete next[fieldName];
                  return next;
                });
              }}
              onChange={(e) => {
                const rawValue = e.target.value;
                setAmountFieldRawValues(prev => {
                  const updated = { ...prev };
                  updated[fieldName] = rawValue;
                  return updated;
                });
              }}
              placeholder="0.00"
              isInvalid={!!fieldError}
              inputMode="decimal"
            />
          </InputGroup>
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    const isNumericOnlyField =
      (typeof value === 'number' ||
        fieldName.toLowerCase().includes('id') ||
        fieldName.toLowerCase().includes('number')) &&
      !['calcapLoanNumber', 'lenderLoanNumber'].includes(fieldName) &&
      !PERCENTAGE_FIELDS.has(fieldName) &&
      !(isCalcapProgram && CALCAP_PERCENT_POINT_FIELDS.has(fieldName));
    if (isNumericOnlyField) {
      return (
        <Col key={fieldName} md={6} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            type="number"
            value={draftDetails[fieldName] ?? ''}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => {
              const numValue = e.target.value === '' ? null : Number(e.target.value);
              handleFieldChange(fieldName, numValue);
            }}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'loanPurpose') {
      return (
        <Col key={fieldName} md={8} lg={8} className="mb-3">
          <MultiSelectDropdown
            name={fieldName}
            label={displayName}
            value={draftDetails[fieldName] ?? ''}
            options={PRIMARY_PURPOSE_OPTIONS}
            separator={LOAN_PURPOSE_SEPARATOR}
            onChange={(nextValue: string | null) => handleFieldChange(fieldName, nextValue)}
            disabled={isReadOnly || isContentLoading}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'loanPurposeSecondary') {
      return (
        <Col key={fieldName} md={8} lg={8} className="mb-3">
          <MultiSelectDropdown
            name={fieldName}
            label={displayName}
            value={draftDetails[fieldName] ?? ''}
            options={SECONDARY_PURPOSE_OPTIONS}
            separator={LOAN_PURPOSE_SEPARATOR}
            onChange={(nextValue: string | null) => handleFieldChange(fieldName, nextValue)}
            disabled={isReadOnly || isContentLoading}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    if (fieldName === 'loanPurposeOther' || fieldName === 'loanPurposeSecondaryOther') {
      return (
        <Col key={fieldName} md={4} lg={4} className="mb-3">
          <Form.Label>{labelContent}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={draftDetails[fieldName] ?? ''}
            disabled={isReadOnly || isContentLoading}
            onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
            isInvalid={!!fieldError}
          />
          {renderFieldError(fieldError)}
        </Col>
      );
    }

    const colProps = getStandardFieldColProps(fieldName);
    return (
      <Col key={fieldName} md={colProps.md} lg={colProps.lg} className="mb-3">
        <Form.Label>{labelContent}</Form.Label>
        <Form.Control
          type="text"
          value={draftDetails[fieldName] ?? ''}
          disabled={isReadOnly || isContentLoading}
          onChange={(e) => handleFieldChange(fieldName, e.target.value || null)}
          isInvalid={!!fieldError}
        />
        {renderFieldError(fieldError)}
      </Col>
    );
  }, [
    programKind,
    excludedFields,
    readOnlyFields,
    draftDetails,
    fieldErrors,
    isContentLoading,
    isSaving,
    applicationDetails,
    edoUsers,
    lenderContacts,
    registeredLendersForSelect,
    getFundingProgram,
    isAmountField,
    parseCurrency,
    handleFieldChange,
    renderFieldError,
    amountFieldRawValues,
    focusedAmountFields,
    focusedPercentageFields,
    percentageFieldRawValues,
    focusedCalcapPercentPointFields,
    calcapPercentPointRawValues,
    lenderApplicationDetailsReadOnly,
    APPLICATION_STATUS,
    history,
    portalType,
    PORTAL_TYPES,
    setAmountFieldRawValues,
    setFocusedAmountFields,
    setFocusedPercentageFields,
    setPercentageFieldRawValues,
    setFocusedCalcapPercentPointFields,
    setCalcapPercentPointRawValues,
  ]);

  return { renderField, renderFieldError };
}
